import { useState, useEffect } from "react";
import { FaBullhorn, FaUsers, FaSearch } from "react-icons/fa";
import Modal from "../modal/modal";
import Dropdown from "../../components/modal/dropdown";
import api from "../services/api";
import { createNotification } from "../services/notificationservice";
import { ANNOUNCEMENT_PRIORITY_OPTIONS, getInitials } from "./notification_utils";

/**
 * Announcement section — "Send Announcement" modal, split out of
 * notifications.jsx. Owns its own form state, validation, and user list
 * fetch, and only talks to the parent through onClose/onSent.
 *
 * Classnames (form-section-card / section-title-premium / attendance-form-body /
 * full-row / grid-2 / field-error / input-error / modal-footer / modal-actions)
 * match AttendanceModal so this reuses attendance.css instead of needing its
 * own bespoke form styles. recipient-option-card / employee-select-list stay
 * notification-specific since AttendanceModal has no equivalent UI.
 */
const AnnouncementModal = ({ onClose, onSent }) => {
  const [usersList, setUsersList] = useState([]);
  const [createData, setCreateData] = useState({
    title: "",
    message: "",
    priority: "2",
    recipient_ids: [],
    all_users: true,
  });
  const [createError, setCreateError] = useState("");
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");

  useEffect(() => {
    api
      .get("users/?page_size=200")
      .then((res) => {
        const list = res.data?.data || res.data?.results || [];
        setUsersList(list);
      })
      .catch(() => {});
  }, []);

  const selectedRecipientCount = createData.all_users ? usersList.length : createData.recipient_ids.length;

  const filteredUsers = (() => {
    const term = employeeSearch.trim().toLowerCase();
    if (!term) return usersList;
    return usersList.filter((u) => {
      const name = (u.full_name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  })();

  const clearFieldError = (key) => {
    if (createFieldErrors[key]) {
      setCreateFieldErrors((prev) => ({ ...prev, [key]: null }));
    }
  };

  const handleCreateSubmit = async () => {
    const errs = {};
    if (!createData.title.trim()) errs.title = "Announcement title is required";
    if (!createData.message.trim()) errs.message = "Message content is required";

    let recipientIds = createData.recipient_ids;
    if (createData.all_users) {
      recipientIds = usersList.map((u) => u.id);
    }
    if (recipientIds.length === 0) {
      errs.recipients = "Please select at least one recipient";
    }

    if (Object.keys(errs).length > 0) {
      setCreateFieldErrors(errs);
      setCreateError("Please check the field errors below.");
      return;
    }
    setCreateError("");
    setCreateFieldErrors({});

    setCreateSubmitting(true);
    try {
      await createNotification({
        title: createData.title,
        message: createData.message,
        priority: parseInt(createData.priority),
        notification_type: 11, // GENERAL_ANNOUNCEMENT
        recipient_ids: recipientIds,
      });
      onSent(recipientIds.length);
    } catch (err) {
      const respData = err?.response?.data;
      const backendErrors = respData?.errors || (respData && typeof respData === "object" && !respData.message && !respData.detail ? respData : null);
      if (backendErrors && typeof backendErrors === "object") {
        const formatted = {};
        for (const key in backendErrors) {
          const val = backendErrors[key];
          formatted[key] = Array.isArray(val) ? val[0] : val;
        }
        setCreateFieldErrors(formatted);
      }
      setCreateError(err?.response?.data?.message || err?.response?.data?.detail || "Please check the field errors below.");
    } finally {
      setCreateSubmitting(false);
    }
  };

  return (
    <Modal title="Send Announcement Notification" icon={<FaBullhorn />} onClose={onClose} size="xl">
      {createError && <div className="form-error">{createError}</div>}

      <div className="attendance-form-body">
        {/* ANNOUNCEMENT DETAILS */}
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaBullhorn /> Announcement Details
          </div>

          <div className="grid-2">
            <div>
              <label>Announcement Title *</label>
              <input
                type="text"
                className={createFieldErrors.title ? "input-error" : ""}
                placeholder="e.g. Office Closed on Friday"
                value={createData.title}
                onChange={(e) => {
                  setCreateData({ ...createData, title: e.target.value });
                  clearFieldError("title");
                }}
              />
              {createFieldErrors.title && <div className="field-error">{createFieldErrors.title}</div>}
            </div>

            <div>
              <label>Priority Level</label>
              <Dropdown
                options={ANNOUNCEMENT_PRIORITY_OPTIONS}
                value={createData.priority}
                onChange={(val) => setCreateData({ ...createData, priority: val })}
                placeholder="Priority"
              />
            </div>
          </div>

          <div className="full-row">
            <label style={{ marginTop: '10px' }}>Message Content *</label>
            <textarea
              rows={5}
              className={createFieldErrors.message ? "input-error" : ""}
              placeholder="Enter details of the announcement..."
              value={createData.message}
              onChange={(e) => {
                setCreateData({ ...createData, message: e.target.value });
                clearFieldError("message");
              }}
            />
            {createFieldErrors.message && <div className="field-error">{createFieldErrors.message}</div>}
          </div>
        </div>

        {/* RECIPIENTS */}
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaUsers /> Recipients
            <span className="selection-summary-chip" style={{ marginLeft: "auto" }}>
              {selectedRecipientCount} selected
            </span>
          </div>

          <div className="recipient-options">
            <label className={`recipient-option-card ${createData.all_users ? "active" : ""}`}>
              <input
                type="radio"
                name="recipients_type"
                checked={createData.all_users}
                onChange={() => {
                  setCreateData({ ...createData, all_users: true });
                  setEmployeeSearch("");
                  clearFieldError("recipients");
                }}
              />
              <div className="recipient-option-content">
                <span className="recipient-option-title">All Active Employees</span>
                <span className="recipient-option-count">{usersList.length} employees</span>
              </div>
            </label>

            <label className={`recipient-option-card ${!createData.all_users ? "active" : ""}`}>
              <input
                type="radio"
                name="recipients_type"
                checked={!createData.all_users}
                onChange={() => setCreateData({ ...createData, all_users: false })}
              />
              <div className="recipient-option-content">
                <span className="recipient-option-title">Select Specific Employees</span>
                <span className="recipient-option-count">Choose individually</span>
              </div>
            </label>
          </div>
          {createFieldErrors.recipients && <div className="field-error">{createFieldErrors.recipients}</div>}

          {!createData.all_users && (
            <div className="employee-select-list">
              <div className="employee-search-wrap">
                <div className="employee-search-field">
                  <FaSearch className="employee-search-icon" />
                  <input
                    type="text"
                    className="employee-search-input"
                    placeholder="    Search employees by name or email..."
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="employee-select-header">
                <input
                  type="checkbox"
                  checked={filteredUsers.length > 0 && filteredUsers.every((u) => createData.recipient_ids.includes(u.id))}
                  onChange={(e) => {
                    const filteredIds = filteredUsers.map((u) => u.id);
                    setCreateData({
                      ...createData,
                      recipient_ids: e.target.checked
                        ? Array.from(new Set([...createData.recipient_ids, ...filteredIds]))
                        : createData.recipient_ids.filter((id) => !filteredIds.includes(id)),
                    });
                    clearFieldError("recipients");
                  }}
                />
                <span>{employeeSearch ? `Select All (${filteredUsers.length} filtered)` : "Select All Employees"}</span>
              </div>

              <div className="employee-select-body">
                {filteredUsers.length === 0 ? (
                  <div className="employee-search-empty">No employees match "{employeeSearch}"</div>
                ) : (
                  filteredUsers.map((u) => {
                    const checked = createData.recipient_ids.includes(u.id);
                    const displayName = u.full_name || u.email;
                    const toggle = () => {
                      const ids = checked
                        ? createData.recipient_ids.filter((id) => id !== u.id)
                        : [...createData.recipient_ids, u.id];
                      setCreateData({ ...createData, recipient_ids: ids });
                      clearFieldError("recipients");
                    };
                    return (
                      <div
                        key={u.id}
                        className={`employee-select-row ${checked ? "selected" : ""}`}
                        onClick={toggle}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={toggle}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="employee-avatar-chip">{getInitials(displayName)}</div>
                        <div>
                          <div className="employee-select-name">{displayName}</div>
                          <div className="employee-select-role">{u.role || u.email}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="modal-footer">
        <div className="modal-actions">
          <button type="button" className="primary" disabled={createSubmitting} onClick={handleCreateSubmit}>
            {createSubmitting ? "Sending..." : `Send Announcement (${selectedRecipientCount})`}
          </button>
          <button type="button" className="secondary" onClick={onClose} disabled={createSubmitting}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AnnouncementModal;